import React from 'react'

import { Brief } from '@disco/types/types/brief'
import { formatTimestamp } from '@disco/utils/time-utils'

import { Box, Text } from 'components/ds'

/**
 * Props for the BriefCard component
 */
interface BriefCardProps {
  /** Brief information to display */
  brief: Brief
}

/**
 * BriefCard - A simple card component for displaying brief information
 *
 * This component renders a minimal brief card showing the title and optional timestamp.
 * It follows the Figma design specifications for brief sharing with the avatar omitted
 * per requirements. The card is designed to be displayed within the ShareBriefModal
 * to give users context about what they're sharing.
 *
 * Features:
 * - Displays brief title with ellipsis overflow handling
 * - Shows optional timestamp in gray text
 * - Fallback to "Brief title" if no title is provided
 * - Tooltip on title for full text when truncated
 * - Simple vertical layout with proper spacing
 *
 * Design compliance:
 * - Uses design system components (Box, Text)
 * - Follows Figma design patterns
 * - Consistent with other card components in the application
 *
 * @param props - Component props
 * @returns BriefCard component
 *
 * @example
 * ```tsx
 * <BriefCard
 *   brief={{
 *     id: 123,
 *     name: "My Brief Title",
 *     updatedAt: "Dec 20, 2024, 6:51AM"
 *   }}
 * />
 * ```
 */
export const BriefCard: React.FC<BriefCardProps> = ({ brief }) => {
  const { name, created } = brief

  const timestamp = created
  return (
    <Box>
      <Box>
        <Box>
          <Text size="2" weight="medium" title={name}>
            {name || 'Brief title'}
          </Text>
        </Box>

        {timestamp && (
          <Box>
            <Text size="2" color="gray">
              {formatTimestamp(timestamp)}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  )
}
